/**
 * agentService.js — Vasari Agent API client + SSE stream consumer.
 *
 * All functions receive `authenticatedFetch` from AuthContext.
 * SSE streaming uses native fetch + ReadableStream.
 */

const BASE = '/api/agent';

// ---------------------------------------------------------------------------
// Session management
// ---------------------------------------------------------------------------

export const createSession = async (authenticatedFetch, pageContext) => {
  const res = await authenticatedFetch(`${BASE}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ page_context: pageContext }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to create session');
  }
  return res.json();
};

export const getSessions = async (authenticatedFetch) => {
  const res = await authenticatedFetch(`${BASE}/sessions`);
  if (!res.ok) throw new Error('Failed to load sessions');
  const data = await res.json();
  return data.sessions || [];
};

export const getSession = async (authenticatedFetch, sessionId) => {
  const res = await authenticatedFetch(`${BASE}/sessions/${sessionId}`);
  if (!res.ok) throw new Error('Failed to load session');
  return res.json();
};

export const killSession = async (authenticatedFetch, sessionId) => {
  const res = await authenticatedFetch(`${BASE}/sessions/${sessionId}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) throw new Error('Failed to cancel session');
};

export const deleteSession = async (authenticatedFetch, sessionId) => {
  const res = await authenticatedFetch(`${BASE}/sessions/${sessionId}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) throw new Error('Failed to delete session');
};

export const cancelPlan = async (authenticatedFetch, sessionId) => {
  const res = await authenticatedFetch(`${BASE}/sessions/${sessionId}/cancel-plan`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to cancel plan');
};

export const undoSession = async (authenticatedFetch, sessionId) => {
  const res = await authenticatedFetch(`${BASE}/sessions/${sessionId}/undo`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to undo session');
  }
  return res.json();
};

// ---------------------------------------------------------------------------
// Temp document upload
// ---------------------------------------------------------------------------

export const uploadTempDocument = async (authenticatedFetch, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await authenticatedFetch(`${BASE}/uploads/temp`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to upload file');
  }
  return res.json();
};

// ---------------------------------------------------------------------------
// SSE stream helpers
// ---------------------------------------------------------------------------

/**
 * Parse a raw SSE line into an event object.
 * SSE format: "data: {...json...}\n\n"
 */
const parseSSELine = (line) => {
  if (!line.startsWith('data: ')) return null;
  try {
    return JSON.parse(line.slice(6));
  } catch {
    return null;
  }
};

/**
 * Generic SSE consumer. Calls onEvent(parsedEvent) for each event.
 * Returns an abort function for cleanup.
 */
const consumeSSE = (fetchPromise, onEvent, onError, onDone) => {
  const controller = new AbortController();
  let cancelled = false;

  (async () => {
    try {
      const res = await fetchPromise(controller.signal);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        onError(new Error(err.detail || `HTTP ${res.status}`));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (cancelled) break;
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          const event = parseSSELine(line.trim());
          if (event) onEvent(event);
        }
      }

      // Flush remaining buffer
      if (buffer.trim()) {
        const event = parseSSELine(buffer.trim());
        if (event) onEvent(event);
      }

      if (!cancelled && onDone) onDone();
    } catch (err) {
      if (!cancelled) onError(err);
    }
  })();

  return () => {
    cancelled = true;
    controller.abort();
  };
};

// ---------------------------------------------------------------------------
// Message streaming
// ---------------------------------------------------------------------------

export const streamMessage = (authenticatedFetch, sessionId, payload, onEvent, onError, onDone) => {
  const fetchFn = (signal) =>
    authenticatedFetch(`${BASE}/sessions/${sessionId}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });

  return consumeSSE(fetchFn, onEvent, onError, onDone);
};

// ---------------------------------------------------------------------------
// Plan confirmation streaming
// ---------------------------------------------------------------------------

export const confirmPlan = (authenticatedFetch, sessionId, selectedStepIndices, onEvent, onError, onDone) => {
  const fetchFn = (signal) =>
    authenticatedFetch(`${BASE}/sessions/${sessionId}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_step_indices: selectedStepIndices }),
      signal,
    });

  return consumeSSE(fetchFn, onEvent, onError, onDone);
};

// ---------------------------------------------------------------------------
// Search helpers for @mention autocomplete
// ---------------------------------------------------------------------------

export const searchClients = async (authenticatedFetch, query) => {
  const res = await authenticatedFetch(`/api/clients`);
  if (!res.ok) return [];
  const data = await res.json();
  const all = data.clients || data || [];
  const filtered = query
    ? all.filter((c) => (c.client_name || c.name || '').toLowerCase().includes(query.toLowerCase()))
    : all;
  return filtered.slice(0, 4).map((c) => ({
    type: 'client',
    id: c.id,
    display_name: c.client_name || c.name,
    subtitle: `${c.portfolio_count || 0} portfolios`,
  }));
};

export const searchPortfolios = async (authenticatedFetch, query) => {
  const res = await authenticatedFetch(`/api/portfolios`);
  if (!res.ok) return [];
  const data = await res.json();
  const all = data.portfolios || data || [];
  const filtered = query
    ? all.filter((p) => (p.name || '').toLowerCase().includes(query.toLowerCase()))
    : all;
  return filtered.slice(0, 4).map((p) => ({
    type: 'portfolio',
    id: p.id,
    display_name: p.name,
    subtitle: `${p.holding_count || 0} artworks`,
  }));
};

export const searchHoldings = async (authenticatedFetch, query) => {
  const res = await authenticatedFetch(`/api/artworks/search?q=${encodeURIComponent(query)}`);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.artworks || data || []).slice(0, 4).map((a) => ({
    type: 'holding',
    id: a.id,
    display_name: `${a.title} — ${a.artist}`,
    subtitle: a.year ? String(a.year) : '',
  }));
};

export const searchDocuments = async (authenticatedFetch, query) => {
  const res = await authenticatedFetch(`/api/documents?search=${encodeURIComponent(query)}`).catch(() => null);
  if (!res || !res.ok) return [];
  const data = await res.json();
  return (data.documents || data || []).slice(0, 4).map((d) => ({
    type: 'document',
    id: d.id,
    display_name: d.file_name || d.id,
    subtitle: d.document_type || '',
  }));
};
